import { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Pencil, RotateCcw, X, FileText, Landmark, Receipt } from 'lucide-react';
import { useToast } from './Toast';
import { api, WORKER_API_BASE, iframeClientParam } from '../lib/api';
import { tr } from '../lib/i18nHelpers';
import { buildCoaTree, CoaNode } from '../lib/coa-hierarchy';
import { auditEventLabel, paymentVariance } from '../lib/auditTrailHelpers';
import { Tag, LedgerBlock, TempCancelNote, money } from './ledger-shared';

interface JeLine { account_code: string; account_name: string; debit: number; credit: number; account_type?: string | null }
interface JournalEntry {
  id: string; entry_number: string; entry_date: string; description: string | null;
  reference_type: string; reference_id?: string | null; status: string; entry_source: string; lines: JeLine[];
}
interface LinkedTx {
  id: string; transaction_date: string; description: string; bank_name: string | null;
  statement_id?: string | null; statement_file_name?: string | null;
  statement_year?: number | null; statement_month?: number | null;
  amount: number; allocated_amount: number | null; match_status: string; link_type: 'direct' | 'group';
  payment_voucher_no: string | null;
}
interface InvoiceDetail {
  id: string; invoice_number: string; total: number; currency: string; direction?: string;
  file_id?: string | null; source_page?: number | null;
  linked_transactions?: LinkedTx[]; journal_entries?: JournalEntry[];
  linked_receipt?: { id: string; invoice_number: string; total: number; issue_date: string } | null;
}
interface MatchSuggestion {
  transaction_id: string;
  invoice_id?: string | null;
  invoice_ids?: string[];
  invoices?: { invoice_number: string; total: number }[];
  invoice_number?: string;
  amount: number;
  confidence?: string;
  reason?: string;
}

export default function AuditTrailModal({ open, onClose, invoiceId, txContext }: {
  open: boolean; onClose: () => void;
  invoiceId?: string | null;
  txContext?: { statementName: string | null; transactionDate: string; description: string; amount: number; matchStatus: string; invoiceIds: string[]; txId?: string; isDeposit: boolean } | null;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();

  const ids = useMemo(() => {
    if (invoiceId) return [invoiceId];
    return txContext?.invoiceIds ?? [];
  }, [invoiceId, txContext]);

  const details = useQuery({
    queryKey: ['audit-trail', ids],
    queryFn: async () => {
      const results = await Promise.allSettled(ids.map(id => api(`/invoices/${id}`)));
      return {
        invoices: results.filter(r => r.status === 'fulfilled').map(r => (r as PromiseFulfilledResult<any>).value as InvoiceDetail),
        missing: results.filter(r => r.status === 'rejected').length,
      };
    },
    enabled: open && ids.length > 0,
  });

  const rows: InvoiceDetail[] = details.data?.invoices || [];
  const invoiceCtx = rows[0] || null;
  const noTxs = !txContext && !!invoiceCtx && !(invoiceCtx.linked_transactions || []).length;
  const ctxTxId = txContext?.txId || '';
  const allTxIds = useMemo(() => Array.from(new Set(rows.flatMap(inv => (inv.linked_transactions || []).map(t => t.id)).concat(ctxTxId ? [ctxTxId] : []))), [rows, ctxTxId]);

  // History timeline for every entity in view (invoices + transactions)
  const history = useQuery({
    queryKey: ['audit-log', ids.join(','), allTxIds.join(',')],
    queryFn: () => api(`/invoices/audit-log?ids=${[...ids, ...allTxIds].slice(0, 20).join(',')}`),
    enabled: open && (ids.length > 0 || allTxIds.length > 0),
  });
  const events: any[] = history.data?.events || [];

  const [autoLinking, setAutoLinking] = useState(false);
  const [autoLinkMsg, setAutoLinkMsg] = useState<string | null>(null);
  const [suggestions, setSuggestions] = useState<MatchSuggestion[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState<{ label: string; holding: string }>({ label: '', holding: '' });
  useEffect(() => { if (!open) { setEditingId(null); setDraft({ label: '', holding: '' }); setAutoLinkMsg(null); setSuggestions([]); } }, [open]);
  const { data: accountsData } = useQuery({
    queryKey: ['accounts'],
    queryFn: () => api('/bookkeeping/accounts'),
    enabled: open && editingId !== null,
  });
  const accounts: any[] = accountsData?.data || [];
  const coaTree: CoaNode[] = useMemo(() => buildCoaTree(accounts), [accounts]);

  const refresh = () => {
    ids.forEach(id => queryClient.invalidateQueries({ queryKey: ['invoice', id] }));
    queryClient.invalidateQueries({ queryKey: ['audit-trail', ids] });
    queryClient.invalidateQueries({ queryKey: ['audit-log'] });
    queryClient.invalidateQueries({ queryKey: ['entries'] });
    queryClient.invalidateQueries({ queryKey: ['bank-statements'] });
    queryClient.invalidateQueries({ queryKey: ['invoices-ap'] });
    queryClient.invalidateQueries({ queryKey: ['invoices-ar'] });
    queryClient.invalidateQueries({ queryKey: ['invoices'] });
  };

  const savePostingMut = useMutation({
    mutationFn: (id: string) => api(`/invoices/${id}/posting`, {
      method: 'PUT',
      body: { label_account_code: draft.label, holding_account_code: draft.holding },
    }),
    onSuccess: () => { setEditingId(null); refresh(); toast.success(tr('Posting updated', '分錄已更新', '分录已更新')); },
    onError: (err: any) => toast.error(err?.message || tr('Update failed', '更新失敗', '更新失败')),
  });
  const resetPostingMut = useMutation({
    mutationFn: (id: string) => api(`/invoices/${id}/posting`, { method: 'PUT', body: { reset_to_auto: true } }),
    onSuccess: () => { setEditingId(null); refresh(); toast.info(tr('Reset to auto classification', '已重設為自動分類', '已重设为自动分类')); },
    onError: (err: any) => toast.error(err?.message || tr('Reset failed', '重設失敗', '重设失败')),
  });

  const autoLinkMut = useMutation({
    mutationFn: () => api(`/bank-statements/auto-match?direction=${invoiceCtx?.direction || 'incoming'}`, { method: 'POST' }),
    onSuccess: (res: any) => {
      const invId = invoiceCtx?.id;
      const mine: MatchSuggestion[] = invId ? ((res?.matched || []) as any[])
        .filter((m: any) => m.invoice_id === invId || (m.invoice_ids || []).includes(invId)) : [];
      setSuggestions(mine);
      setAutoLinkMsg(mine.length === 0
        ? tr('No matching transactions found', '未找到配對的銀行交易', '未找到配对的银行交易')
        : tr(`${mine.length} match(es) found — confirm below`, `找到 ${mine.length} 筆配對建議——請在下方確認`, `找到 ${mine.length} 条配对建议——请在下方确认`));
      refresh();
    },
    onError: (e: any) => setAutoLinkMsg(e?.message || tr('Auto-link failed', '自動連結失敗', '自动连结失败')),
    onSettled: () => setAutoLinking(false),
  });

  const confirmTxMut = useMutation({
    mutationFn: ({ txId, invoiceId }: { txId: string; invoiceId?: string }) =>
      api(`/bank-statements/transactions/${txId}/match`, { method: 'PATCH', body: { invoice_id: invoiceId, action: 'confirm' } }),
    onSuccess: (_res, vars) => {
      setSuggestions(prev => prev.filter(s => s.transaction_id !== vars.txId));
      refresh();
      if (txContext) {
        toast.success(tr('Invoice linked', '發票已連結', '发票已链接'));
        onClose();
      }
    },
    onError: (e: any) => toast.error(e?.message || tr('Confirm failed', '確認失敗', '确认失败')),
  });
  const unlinkTxMut = useMutation({
    mutationFn: (txId: string) => api(`/bank-statements/transactions/${txId}/match`, { method: 'PATCH', body: { action: 'unlink' } }),
    onSuccess: () => refresh(),
    onError: (e: any) => toast.error(e?.message || tr('Unlink failed', '解除連結失敗', '解除连结失败')),
  });

  const candidates = useQuery({
    queryKey: ['link-candidates', ctxTxId],
    queryFn: () => api(`/invoices?status=draft,sent,overdue&q=&direction=${txContext?.isDeposit ? 'outgoing' : 'incoming'}`),
    enabled: open && !!ctxTxId && !details.isLoading && rows.length === 0,
  });

  if (!open) return null;

  // Bank side = payment legs across the invoices in view (deduped by entry id)
  const paymentBlocks = Array.from(new Map(
    rows.flatMap(inv => (inv.journal_entries || [])
      .filter(e => e.reference_type === 'payment')
      .map(e => ({ je: e, tx: (inv.linked_transactions || []).find(t => t.id === e.reference_id) || undefined })))
    .map(pb => [pb.je.id, pb])
  ).values());
  const anyTx = txContext ? null : (rows.flatMap(inv => inv.linked_transactions || []).filter(t => t.match_status !== 'unmatched')[0] || null);
  const currency = invoiceCtx?.currency || 'HKD';
  const paidTotal = paymentBlocks.reduce((s, pb) => s + (pb.tx ? (pb.tx.allocated_amount ?? pb.tx.amount) : 0), 0) || txContext?.amount || 0;
  const variance = rows.length ? paymentVariance(paidTotal || txContext?.amount || 0, rows) : null;
  const varianceReason = events.find(e => e.action === 'confirm_match' && e.changes && String(e.changes).includes('review_reason'));

  const openStatementPdf = async (statementId: string) => {
    try {
      const token = localStorage.getItem('token') || '';
      const resp = await fetch(`${WORKER_API_BASE}/bank-statements/${statementId}/file?token=${token}${iframeClientParam()}`);
      if (!resp.ok) { toast.error(tr('Could not open PDF', '無法開啟 PDF', '无法开启 PDF')); return; }
      const url = URL.createObjectURL(await resp.blob());
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch { toast.error(tr('Could not open PDF', '無法開啟 PDF', '无法开启 PDF')); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose} data-testid="audit-trail-modal">
      {/* max-height + internal scroll: header stays pinned, body scrolls */}
      <div className="bg-card border rounded-xl w-full max-w-3xl max-h-[88vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="flex items-center gap-2 px-4 py-3 border-b">
          <h3 className="font-bold text-base">{tr('Audit Trail', '審計追蹤', '审计追踪')}</h3>
          {txContext?.statementName && <span className="text-xs text-muted-foreground truncate">{txContext.statementName}</span>}
          {txContext && <Tag kind={txContext.matchStatus === 'confirmed' ? 'confirmed' : txContext.matchStatus === 'suggested' ? 'suggested' : 'auto'}>{txContext.matchStatus?.toUpperCase()}</Tag>}
          {rows.some(inv => (inv.linked_transactions || []).some(t => t.link_type === 'group')) && <Tag kind="group" />}
          <button onClick={onClose} className="ml-auto p-1 hover:bg-muted rounded"><X className="h-4 w-4" /></button>
        </div>

        <div className="overflow-y-auto flex-1 space-y-4 p-4">

          {/* ── 1. Documents: stacked cards on a rail, no arrows ── */}
          <div className="border rounded-lg p-3" data-testid="audit-chain">
            <p className="text-[11px] uppercase tracking-wide text-muted-foreground font-semibold mb-2.5">{tr('Documents', '文件', '文件')}</p>
            <div className="relative pl-5 before:absolute before:left-[7px] before:top-2 before:bottom-2 before:w-0.5 before:bg-border">
              {/* merged statement + transaction card */}
              {(txContext || anyTx) && (() => {
                const tx = anyTx;
                const stFile = tx?.statement_file_name;
                return (
                  <div className="relative bg-muted/40 border rounded-lg px-3 py-2 mb-2 flex items-center gap-2.5">
                    <span className="absolute -left-[18px] top-3 h-2.5 w-2.5 rounded-full bg-card border-2 border-blue-400" />
                    <Landmark className="h-4 w-4 text-blue-600 shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-semibold truncate">{(txContext?.description || tx?.description || '')}</p>
                      <p className="text-[11px] text-muted-foreground truncate">
                        <span className="font-mono">{txContext?.transactionDate || tx?.transaction_date}</span>
                        {' · '}{txContext?.statementName || tx?.bank_name || tr('Bank statement', '銀行月結單', '银行月结单')}
                        {stFile ? <>{' · '}{tr('from', '來自', '来自')} <b>{stFile}</b></> : null}
                        {tx?.payment_voucher_no ? ` · ${tx.payment_voucher_no}` : ''}
                      </p>
                    </div>
                    <span className="font-mono text-xs font-semibold whitespace-nowrap">{currency} {money(txContext?.amount ?? tx?.amount)}</span>
                    {tx?.statement_id && (
                      <button onClick={() => openStatementPdf(tx.statement_id!)}
                        className="text-[11px] px-2 py-1 rounded-md border border-blue-200 bg-blue-50 text-blue-700 hover:underline shrink-0 dark:bg-blue-900/30 dark:border-blue-800">
                        {tr('View PDF', '查看 PDF', '查看 PDF')}
                      </button>
                    )}
                  </div>
                );
              })()}
              {/* invoices card */}
              {rows.length > 0 && (
                <div className="relative bg-muted/40 border rounded-lg px-3 py-2 flex items-center gap-2.5">
                  <span className="absolute -left-[18px] top-3 h-2.5 w-2.5 rounded-full bg-card border-2 border-green-500" />
                  <Receipt className="h-4 w-4 text-green-900 dark:text-green-200 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-semibold truncate">{rows.map(r => r.invoice_number).join(' + ')}</p>
                    <p className="text-[11px] text-muted-foreground truncate">
                      {rows.length === 1 ? tr('Invoice', '發票', '发票') : tr(`${rows.length} invoices — combined payment`, `${rows.length} 張發票——合併付款`, `${rows.length} 张发票——合并付款`)}
                      {rows.every(r => (r.linked_transactions || []).some(t => t.link_type === 'group')) ? ` · ${rows.map(r => money(r.total)).join(' + ')}` : ''}
                    </p>
                  </div>
                  <span className="font-mono text-xs font-semibold whitespace-nowrap">{currency} {money(rows.reduce((s, r) => s + r.total, 0))}</span>
                </div>
              )}
            </div>

            {details.data && details.data.missing > 0 && (
              <p className="text-xs text-amber-900 dark:text-amber-100 mt-2">{tr(`${details.data.missing} document(s) no longer available`, `${details.data.missing} 份文件已不存在`, `${details.data.missing} 份文件已不存在`)}</p>
            )}
            {noTxs && (
              <div className="flex items-center gap-2 mt-2">
                <button data-testid="auto-link-btn" onClick={() => { setAutoLinking(true); setAutoLinkMsg(null); autoLinkMut.mutate(); }}
                  disabled={autoLinking} className="px-2 py-1 text-xs bg-primary text-primary-foreground rounded hover:opacity-90 disabled:opacity-40">
                  {autoLinking ? '…' : tr('Auto-link', '自動連結', '自动连结')}
                </button>
                <span className="text-[11px] text-muted-foreground">{tr('Find matching bank transactions', '尋找配對的銀行交易', '寻找配对的银行交易')}</span>
              </div>
            )}
            {autoLinkMsg && <p className="text-[11px] text-muted-foreground mt-1">{autoLinkMsg}</p>}
            {invoiceCtx && suggestions.length > 0 && (
              <div className="space-y-1 mt-2" data-testid="auto-link-suggestions">
                {suggestions.map(s => {
                  const isGroup = (s.invoice_ids?.length ?? 0) >= 2;
                  return (
                    <div key={s.transaction_id} className="flex flex-wrap items-center gap-2 text-xs" data-testid="suggested-match">
                      <Tag kind={s.confidence === 'high' ? 'confirmed' : 'auto'}>{(s.confidence || 'LOW').toUpperCase()}</Tag>
                      {isGroup && <Tag kind="group" />}
                      <span className="px-1.5 py-0.5 rounded bg-card border font-medium">
                        {isGroup ? (s.invoices || []).map((i: any) => i.invoice_number).join(' + ') : s.invoice_number}
                      </span>
                      <span className="font-mono">{currency} {money(s.amount)}</span>
                      <span className="text-muted-foreground truncate flex-1 min-w-[120px]">{s.reason}</span>
                      <button data-testid="confirm-suggested-btn" onClick={() => confirmTxMut.mutate({ txId: s.transaction_id, invoiceId: invoiceCtx.id })} disabled={confirmTxMut.isPending}
                        className="p-0.5 hover:bg-green-50 rounded text-green-900 dark:text-green-200 disabled:opacity-40" title={tr('Confirm', '確認', '确认')}>✓</button>
                      <button onClick={() => setSuggestions(prev => prev.filter(x => x.transaction_id !== s.transaction_id))}
                        disabled={confirmTxMut.isPending}
                        className="p-0.5 hover:bg-red-50 rounded text-red-900 dark:text-red-200 disabled:opacity-40" title={tr('Reject', '拒絕', '拒绝')}>✕</button>
                    </div>
                  );
                })}
              </div>
            )}
            {ctxTxId && rows.length === 0 && (
              <div className="space-y-1 mt-2" data-testid="link-candidates">
                <p className="text-[11px] text-muted-foreground">{tr('Candidate invoices to link', '可連結的發票候選', '可连结的发票候选')}:</p>
                {((candidates.data as any)?.data || []).slice(0, 8).map((c: any) => (
                  <div key={c.id} className="flex items-center gap-2 text-xs" data-testid="link-candidate">
                    <span className="font-medium">{c.invoice_number}</span>
                    <span className="text-muted-foreground truncate flex-1">{c.vendor_name || c.customer_name || c.supplier_name || '-'}</span>
                    <span className="font-mono">{c.currency} {money(c.total)}</span>
                    <button onClick={() => confirmTxMut.mutate({ txId: ctxTxId, invoiceId: c.id })} disabled={confirmTxMut.isPending}
                      className="px-2 py-0.5 border rounded text-[11px] hover:bg-muted disabled:opacity-40">{tr('Link', '連結', '连结')}</button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* variance callout */}
          {variance != null && (
            <div className="flex flex-wrap items-center gap-2 text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded-lg px-3 py-2 dark:bg-amber-950/30 dark:border-amber-800 dark:text-amber-200">
              ⚠ <b>{tr('Variance', '差異', '差异')} {variance > 0 ? '+' : ''}{money(variance)}</b>
              {' — '}{tr('payment', '付款', '付款')} {money(paidTotal || txContext?.amount || 0)} vs {tr('invoice total', '發票總額', '发票总额')} {money(rows.reduce((s, r) => s + r.total, 0))}
              {varianceReason && <Tag kind="reviewed" />}
            </div>
          )}

          {/* ── 2. GL postings: bank side vs invoice side ── */}
          {(paymentBlocks.length > 0 || rows.length > 0) && (
            <div className="border rounded-lg p-3">
              <p className="text-[11px] uppercase tracking-wide text-muted-foreground font-semibold mb-2.5">
                {tr('GL postings — where money is debited & credited', '過賬分錄——借貸科目', '过账分录——借贷科目')}
              </p>
              <div className="grid md:grid-cols-2 gap-3">
                {/* bank side */}
                <div className="border rounded-lg overflow-hidden">
                  <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 text-blue-800 text-xs font-bold dark:bg-blue-950/40 dark:text-blue-300">
                    <Landmark className="h-3.5 w-3.5" /> {tr('Bank statement side', '銀行月結單方', '银行月结单方')}
                  </div>
                  {paymentBlocks.length === 0 && (
                    <p className="text-xs text-muted-foreground p-3">
                      {txContext
                        ? tr('No payment entry — the link is not confirmed yet.', '未有付款分錄——連結尚未確認。', '未有付款分录——连结尚未确认。')
                        : tr('No payment entry yet.', '尚未有付款分錄。', '尚未有付款分录。')}
                    </p>
                  )}
                  {paymentBlocks.map(({ je, tx }) => (
                    <div key={je.id}>
                      <div className="flex items-center gap-2 px-3 pt-2 text-[11px]">
                        <span className="font-mono font-semibold">{je.entry_number}</span>
                        <span className="text-muted-foreground font-mono">{je.entry_date}</span>
                        <span className="ml-auto"><Tag kind={je.entry_source === 'manual' ? 'manual' : 'auto'} /></span>
                      </div>
                      <LedgerBlock lines={je.lines} footer={tx ? `${tx.transaction_date} · ${money(tx.allocated_amount ?? tx.amount)}` : undefined} />
                    </div>
                  ))}
                </div>

                {/* invoice side: one full entry PER invoice */}
                <div className="border rounded-lg overflow-hidden">
                  <div className="flex items-center gap-2 px-3 py-2 bg-purple-50 text-purple-800 text-xs font-bold dark:bg-purple-950/40 dark:text-purple-300">
                    <Receipt className="h-3.5 w-3.5" /> {tr('Invoice side', '發票方', '发票方')}{rows.length > 1 ? ` — ${rows.length} ${tr('entries', '分錄', '分录')}` : ''}
                  </div>
                  {rows.map(inv => {
                    const invoiceJe = (inv.journal_entries || []).find(e => e.reference_type === 'invoice') || null;
                    const editing = editingId === inv.id;
                    return (
                      <div key={inv.id} className="border-b last:border-b-0">
                        <div className="flex items-center gap-2 px-3 pt-2 text-[11px]">
                          <span className="font-bold text-xs">{inv.invoice_number}</span>
                          <span className="font-mono font-semibold">{invoiceJe?.entry_number}</span>
                          <span className="ml-auto flex items-center gap-1">
                            {invoiceJe && !editing && (
                              <button data-testid="edit-posting" onClick={() => { setEditingId(inv.id); setDraft({ label: invoiceJe.lines.find(l => ['revenue', 'expense'].includes(String(l.account_type)))?.account_code || '', holding: invoiceJe.lines.find(l => ['asset', 'liability'].includes(String(l.account_type)))?.account_code || '' }); }}
                                className="p-1 hover:bg-muted rounded" title={tr('Edit posting', '編輯分錄', '编辑分录')}>
                                <Pencil className="h-3 w-3" />
                              </button>
                            )}
                            {invoiceJe?.entry_source === 'manual' && !editing && (
                              <button data-testid="reset-posting" onClick={() => resetPostingMut.mutate(inv.id)} disabled={resetPostingMut.isPending}
                                className="p-1 hover:bg-muted rounded text-amber-900 dark:text-amber-100" title={tr('Reset to auto', '重設為自動', '重设为自动')}>
                                <RotateCcw className="h-3 w-3" />
                              </button>
                            )}
                            {invoiceJe && <Tag kind={invoiceJe.entry_source === 'manual' ? 'manual' : 'auto'} />}
                          </span>
                        </div>
                        {invoiceJe ? <LedgerBlock lines={invoiceJe.lines} /> : (
                          <p className="text-xs text-muted-foreground px-3 pb-2">{tr('Not yet posted to GL', '尚未過賬至總賬', '尚未过账至总账')}</p>
                        )}
                        {inv.file_id && (
                          <a href={`${WORKER_API_BASE}/file-storage/${inv.file_id}/download?inline=1&token=${localStorage.getItem('token') || ''}${iframeClientParam()}${inv.source_page ? `#page=${inv.source_page}` : ''}`}
                            target="_blank" rel="noreferrer"
                            className="inline-flex items-center gap-1 text-[11px] px-3 pb-2 text-primary hover:underline">
                            <FileText className="h-3 w-3" /> {tr('View invoice PDF', '查看發票 PDF', '查看发票 PDF')}{inv.source_page ? ` (p.${inv.source_page})` : ''}
                          </a>
                        )}
                        {editing && (
                          <div className="border-t p-2 space-y-2 bg-muted/10">
                            <CoaRoleSelect label={tr('What kind of income / expense', '收入／支出類別', '收入／支出类别')}
                              value={draft.label} onChange={v => setDraft(d => ({ ...d, label: v }))} tree={coaTree} allowedTypes={['revenue', 'expense']} />
                            <CoaRoleSelect label={tr('Where the debt / claim is tracked', '債務／權益追蹤科目', '债务／权益追踪科目')}
                              value={draft.holding} onChange={v => setDraft(d => ({ ...d, holding: v }))} tree={coaTree} allowedTypes={['asset', 'liability']} />
                            {draft.label && draft.holding && draft.label === draft.holding && (
                              <p className="text-xs text-red-900 dark:text-red-200">{tr('Accounts must differ', '兩個科目不能相同', '两个科目不能相同')}</p>
                            )}
                            <div className="flex items-center justify-end gap-2">
                              <button onClick={() => setEditingId(null)} className="px-3 py-1 text-xs border rounded hover:bg-muted">{tr('Cancel', '取消', '取消')}</button>
                              <button onClick={() => savePostingMut.mutate(inv.id)} disabled={savePostingMut.isPending || !draft.label || !draft.holding || draft.label === draft.holding}
                                className="px-3 py-1 text-xs bg-primary text-primary-foreground rounded hover:opacity-90 disabled:opacity-30">
                                {savePostingMut.isPending ? '…' : tr('Save posting', '儲存分錄', '储存分录')}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* flow note: temp account cancels across both sides. The old
                  inline version guarded on isTemporaryAccount('') — always
                  false — so the note never rendered; TempCancelNote fixes that. */}
              <TempCancelNote
                contraLines={paymentBlocks.flatMap(pb => pb.je.lines)}
                otherLines={rows.flatMap(inv => (inv.journal_entries || []).filter(e => e.reference_type === 'invoice').flatMap(e => e.lines))}
              />
            </div>
          )}

          {/* ── 3. History timeline ── */}
          <div className="border rounded-lg p-3">
            <p className="text-[11px] uppercase tracking-wide text-muted-foreground font-semibold mb-2">{tr('History', '歷史', '历史')}</p>
            {history.isLoading && <p className="text-xs text-muted-foreground">{tr('Loading...', '載入中...', '载入中...')}</p>}
            {!history.isLoading && events.length === 0 && (
              <p className="text-xs text-muted-foreground">{tr('No recorded events', '沒有記錄事件', '没有记录事件')}</p>
            )}
            <ul className="space-y-0">
              {events.map(ev => (
                <li key={ev.id} className="relative pl-4 pb-2.5 text-xs before:absolute before:left-[3px] before:top-[6px] before:h-2 before:w-2 before:rounded-full before:bg-border">
                  {auditEventLabel(ev.action, ev.changes)}
                  <span className="text-muted-foreground text-[11px] ml-1.5">
                    {ev.entity_type === 'invoice' ? `· ${tr('invoice', '發票', '发票')}` : ev.entity_type === 'bank_transaction' ? `· ${tr('bank tx', '銀行交易', '银行交易')}` : `· ${ev.entity_type}`}
                    {' · '}{ev.actor}{' · '}{ev.created_at}
                  </span>
                </li>
              ))}
            </ul>
            <div className="flex flex-wrap gap-2 items-center pt-1 text-[11px] text-muted-foreground border-t mt-1 pt-2">
              <span className="inline-block font-mono font-bold text-[10px] px-1.5 py-px rounded bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">Dr</span>{tr('debit', '借', '借')}
              <span className="inline-block font-mono font-bold text-[10px] px-1.5 py-px rounded bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300">Cr</span>{tr('credit', '貸', '贷')}
              <Tag kind="temp" /><Tag kind="auto" /><Tag kind="manual" /><Tag kind="confirmed" /><Tag kind="group" />
            </div>
          </div>

          {details.isLoading && <p className="text-xs text-muted-foreground">{tr('Loading...', '載入中...', '载入中...')}</p>}
        </div>
      </div>
    </div>
  );
}

function CoaRoleSelect({ label, value, onChange, tree, allowedTypes }: {
  label: string; value: string; onChange: (v: string) => void; tree: CoaNode[]; allowedTypes: string[];
}) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</span>
      <select value={value} onChange={e => onChange(e.target.value)} className="w-full mt-0.5 border rounded px-2 py-1 text-xs bg-background">
        <option value="">{tr('-- Select account --', '-- 選科目 --', '-- 選科目 --')}</option>
        {tree.map(n => n.isParent ? (
          <option key={`p-${n.account.account_code}`} value="" disabled>
            {`${' '.repeat(n.depth * 2)}${n.account.account_code} ${n.account.account_name}`}
          </option>
        ) : (
          allowedTypes.includes(String((n.account as any).account_type)) ? (
            <option key={n.account.account_code} value={n.account.account_code}>
              {`${' '.repeat(n.depth * 3)}${n.account.account_code} ${n.account.account_name}`}
            </option>
          ) : null
        ))}
      </select>
    </label>
  );
}
